import './UserGuide.css'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MaterialSymbol } from 'react-material-symbols'

import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'

import { useUserGuideContext } from '../context'

export const UserGuide: React.FC = () => {
  const { t } = useTranslation()
  const { closeUserGuide, userGuideUrl } = useUserGuideContext()
  const [isResizing, setIsResizing] = useState(false)
  const [mouseDown, setMouseDown] = useState(false)

  const openUserGuideInTab = () => window.open(userGuideUrl, '_blank')

  const asideRef = useRef<HTMLDivElement | null>(null)

  const handleMouseDown = useCallback(() => {
    setMouseDown(true)
  }, [])

  const handleMouseUp = useCallback(() => {
    setMouseDown(false)
    if (isResizing) {
      setIsResizing(false)
    }
  }, [isResizing])

  const handleResize = useCallback(() => {
    if (mouseDown) {
      setIsResizing(true)
    }
  }, [mouseDown])

  // Block iframe with pointer-events-none because if the
  // splitter is dragged over the iframe the focus is lost and
  // it is not possible to make it smaller. We wait that there
  // is a callBack for splitter like onResizeStart but there is
  // only onResizeEnd. (23.01.2024)
  useEffect(() => {
    window.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mouseup', handleMouseUp)

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(asideRef?.current!)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseDown, handleMouseUp, handleResize])

  return (
    <aside ref={asideRef} className="aside">
      <header className="aside__header flex align-items-center pl-3">
        <h3 className="m-0 mr-auto">{t('common.titles.userGuide')}</h3>

        {/* Open user guide in new tab */}
        <Button
          link
          aria-label="Open user guide in a new tab"
          onClick={openUserGuideInTab}
          icon={<MaterialSymbol icon="open_in_new" size={24} />}
        />

        {/* Close user guide */}
        <Button
          link
          rounded
          aria-label="Close user guide"
          onClick={closeUserGuide}
          icon={<MaterialSymbol icon="close" size={24} />}
        />
      </header>

      {/* User guide */}
      <iframe
        src={userGuideUrl}
        key={userGuideUrl} // Don't delete this, it helps with the back navigation otherwise you need to click twice on the back button
        title="User guide"
        name="user-guide"
        id="user-guide"
        allowFullScreen // Videos can be viewed at full screen
        loading="lazy"
        className={classNames('aside__user-guide', {
          'pointer-events-none': isResizing,
        })}
      />
    </aside>
  )
}
